'use client';

import {
	Event,
	EventType,
	OrderAction,
	OrderActionRecord,
	PublicKey,
	WrappedEvent,
} from '@drift-labs/sdk';
import {
	DepositRecordEvent,
	ENUM_UTILS,
	FundingPaymentRecordEvent,
	LiquidationRecordEvent,
	OrderRecordEvent,
	Serializer,
	SettlePnlRecordEvent,
	SwapRecordEvent,
	UISerializableOrderActionRecord,
	getSortScoreForOrderActionRecords,
	matchEnum,
} from '@drift/common';
import { OrderFillTimeTracker } from '../../utils/orderFillTimeTracker';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { OrderMatcherContext } from 'src/providers/orderRecordMatcherProvider';
import { notify } from 'src/utils/notifications';
import { getSwapToastId } from 'src/utils/swap';
import { getCleanedMarketSymbol } from 'src/utils/market';
import { DriftBlockchainEventSubjectContext } from '../../providers/driftEvents/driftEventSubjectProvider';
import useDriftStore from '../../stores/DriftStore/useDriftStore';
import useAppEventEmitter from '../useAppEventEmitter';
import useLazySubAccounts from '../useLazySubAccounts';
import OrderRecordAndActionStateHandler from '../../utils/OrderRecordAndActionMatcher';
import { MAX_RECENT_TRADES_TO_CACHE } from '../../constants/constants';
import { useSpotMarketsSortedByMarketIndex } from '../spotMarkets/useSpotMarketsSortedByMarketIndex';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';
import { useQueryClient, type InfiniteData } from '@tanstack/react-query';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import useCurrentAuthority from '../useCurrentAuthority';
import { convertOrderRecordToParentOrderInfo } from 'src/utils/historyTables';
import { optimisticallyInsertShortTermRecord } from '../historyData/optimisticInsert';
import { createQueryKey } from 'src/utils/query/createQueryKey';

/**
 * This hook is responsible for listening to the drift events that come through the relevant event subject. All subscription optimisation should be done BEFORE the events get piped into these subjects.
 */

const EVENTS_TO_LISTEN_TO = [
	'OrderActionRecord',
	'OrderRecord',
	'SwapRecord',
	'DepositRecord',
	'FundingPaymentRecord',
	'LiquidationRecord',
	'SettlePnlRecord',
];

const TRADE_DEDUPE_MARKER = 20;

const useDriftMarketEventListener = () => {
	const eventSubjectContext = useContext(DriftBlockchainEventSubjectContext);
	const spotMarketsSortedByMarketIndex = useSpotMarketsSortedByMarketIndex();
	const queryClient = useQueryClient();

	const getDriftState = useDriftStore((s) => s.get);
	const setDriftState = useDriftStore((s) => s.set);

	const currentAccountPubKey =
		useDriftAccountStore((s) =>
			s.getCurrentUserAccount()?.pubKey?.toString()
		) ?? '';
	const currentAuthority = useCurrentAuthority()?.toString() ?? '';

	const currentAccountPubKeyRef = useRef(currentAccountPubKey);
	const currentAuthorityRef = useRef(currentAuthority);
	currentAccountPubKeyRef.current = currentAccountPubKey;
	currentAuthorityRef.current = currentAuthority;

	const accounts = useLazySubAccounts();

	const appEventEmitter = useAppEventEmitter();

	const orderMatcher = useContext(OrderMatcherContext);

	useEffect(() => {
		OrderFillTimeTracker.setOrderFilledChecker((orderId, userAccountKey) => {
			const queries = queryClient.getQueriesData<
				InfiniteData<{
					records: UISerializableOrderActionRecord[];
					nextPage: string | null;
				}>
			>({
				queryKey: createQueryKey('short-term-history', 'trades'),
			});
			for (const [, data] of queries) {
				if (!data?.pages) continue;
				for (const page of data.pages) {
					const found = page.records.some(
						(trade) =>
							(trade.taker?.toString() === userAccountKey &&
								trade.takerOrderId === orderId) ||
							(trade.maker?.toString() === userAccountKey &&
								trade.makerOrderId === orderId)
					);
					if (found) return true;
				}
			}
			return false;
		});
	}, [queryClient]);

	const findAccountToAppendTo = useCallback(
		(pubKey: PublicKey): string | undefined => {
			if (!accounts?.length || !pubKey) return;
			return accounts.find((acct) => acct?.pubKey?.equals(pubKey))?.userKey;
		},
		[accounts]
	);

	const appendDepositRecord = (newDepositRecord: DepositRecordEvent) => {
		const matchingUserKey = findAccountToAppendTo(newDepositRecord.user);
		if (matchingUserKey === undefined) return;

		const formattedRecord = Serializer.Deserialize.UIDeposit(
			Serializer.Serialize.Deposit(newDepositRecord)
		);
		if (newDepositRecord.user?.toString() === currentAccountPubKeyRef.current) {
			optimisticallyInsertShortTermRecord(
				queryClient,
				'deposits',
				currentAccountPubKeyRef.current,
				currentAuthorityRef.current,
				formattedRecord
			);
		}
		appEventEmitter.emit('newDepositRecord', {
			depositRecord: formattedRecord,
		});
	};

	const appendTradeRecord = (
		newOrderActionRecord: Event<OrderActionRecord>
	) => {
		// If the action wasn't a fill, doesn't affect trade history
		if (!matchEnum(newOrderActionRecord.action, OrderAction.FILL)) {
			return;
		}

		// # Handling if it's a trade for the current user
		const matchingTaker = newOrderActionRecord.taker
			? findAccountToAppendTo(newOrderActionRecord.taker)
			: undefined;
		const matchingMaker = newOrderActionRecord.maker
			? findAccountToAppendTo(newOrderActionRecord.maker)
			: undefined;

		const matchingUserKey =
			matchingTaker !== undefined
				? matchingTaker
				: matchingMaker !== undefined
					? matchingMaker
					: undefined;

		const matchingPubKey =
			matchingTaker !== undefined
				? newOrderActionRecord.taker
				: newOrderActionRecord.maker;

		const serializedOrderActionRecord =
			Serializer.Serialize.OrderActionRecord(newOrderActionRecord);
		const formattedOrderActionRecord =
			Serializer.Deserialize.UIOrderActionRecord(serializedOrderActionRecord);

		// Track order fill time
		if (matchingUserKey !== undefined) {
			const userOrderId =
				matchingTaker !== undefined
					? newOrderActionRecord.takerOrderId
					: newOrderActionRecord.makerOrderId;

			if (userOrderId !== null) {
				const auctionVersion = useFeatureFlagStore
					.getState()
					.flagEnabled('AUCTION_PARAMS_VERSION')
					? 2
					: 1;

				OrderFillTimeTracker.trackOrderFill(
					userOrderId,
					Date.now(),
					newOrderActionRecord.txSig ?? undefined,
					matchingPubKey?.toString() ?? undefined,
					auctionVersion
				);
			}
		}

		if (matchingTaker !== undefined) {
			orderMatcher?.addActionRecord(formattedOrderActionRecord);
		}

		if (matchingUserKey !== undefined) {
			const isCurrentAccountTrade =
				newOrderActionRecord.taker?.toString() ===
					currentAccountPubKeyRef.current ||
				newOrderActionRecord.maker?.toString() ===
					currentAccountPubKeyRef.current;
			if (isCurrentAccountTrade) {
				optimisticallyInsertShortTermRecord(
					queryClient,
					'trades',
					currentAccountPubKeyRef.current,
					currentAuthorityRef.current,
					formattedOrderActionRecord
				);
				queryClient.invalidateQueries({
					queryKey: ['positionHistory'],
				});
			}
			appEventEmitter.emit('orderTradeRecordReceived');
		}

		// # Handling for global trades, adding to recent trades list etc.

		const selectedUiMarket = getDriftState().selectedMarket.current;

		const tradeMatchesSelectedMarket =
			selectedUiMarket?.market.marketIndex ===
				formattedOrderActionRecord.marketIndex &&
			ENUM_UTILS.match(
				selectedUiMarket.marketType,
				formattedOrderActionRecord.marketType
			);

		if (!tradeMatchesSelectedMarket) {
			return;
		}

		// Add trade record to market trade history
		const marketTrades = getDriftState().marketTradeHistory?.trades;

		const mostRecentSeenTrade = marketTrades?.[0];

		const tradeIsNew = !mostRecentSeenTrade
			? true
			: getSortScoreForOrderActionRecords(
					newOrderActionRecord,
					mostRecentSeenTrade
				) === 1;

		if (!tradeIsNew) {
			return;
		}

		const filteredMarketTrades = marketTrades.filter(
			(trade) => trade.slot >= newOrderActionRecord.slot
		);

		const tradeIsDuplicate = filteredMarketTrades.find((tradeInStore) =>
			orderMatcher?.ordersAreIdentical(tradeInStore, formattedOrderActionRecord)
		);

		if (tradeIsDuplicate) {
			return;
		}

		setDriftState((s) => {
			if (
				s.selectedMarket.current?.market.marketIndex !==
					formattedOrderActionRecord.marketIndex ||
				!ENUM_UTILS.match(
					s.selectedMarket.current.marketType,
					formattedOrderActionRecord.marketType
				)
			) {
				return;
			}

			let newTradesList = [formattedOrderActionRecord, ...marketTrades];

			if (newTradesList.length % TRADE_DEDUPE_MARKER === 0) {
				newTradesList =
					OrderRecordAndActionStateHandler.getSortedDedupedTrimmedRecords(
						newTradesList as UISerializableOrderActionRecord[],
						MAX_RECENT_TRADES_TO_CACHE
					);
			}

			s.marketTradeHistory.trades =
				newTradesList as UISerializableOrderActionRecord[];
		});
	};

	const appendOrderRecord = (newOrderRecord: OrderRecordEvent) => {
		const matchingUserKey = findAccountToAppendTo(newOrderRecord.user);

		if (matchingUserKey === undefined) {
			return;
		}

		const formattedEvent = Serializer.Deserialize.UIOrderRecord(
			Serializer.Serialize.OrderRecord(newOrderRecord)
		);

		orderMatcher?.addOrderRecord(formattedEvent);

		const parentOrderInfo = convertOrderRecordToParentOrderInfo(formattedEvent);
		if (newOrderRecord.user?.toString() === currentAccountPubKeyRef.current) {
			optimisticallyInsertShortTermRecord(
				queryClient,
				'orders',
				currentAccountPubKeyRef.current,
				currentAuthorityRef.current,
				parentOrderInfo
			);
		}
		appEventEmitter.emit('orderTradeRecordReceived');
	};

	const appendSwapRecord = (newSwapRecord: SwapRecordEvent) => {
		const matchingUserKey = findAccountToAppendTo(newSwapRecord.user);

		if (matchingUserKey === undefined) {
			return;
		}

		const formattedEvent = Serializer.Deserialize.UISwapRecord(
			Serializer.Serialize.SwapRecord(newSwapRecord)
		);

		const toastId = getSwapToastId(
			formattedEvent.inMarketIndex,
			formattedEvent.outMarketIndex
		);

		const shouldNotify = getDriftState().listenForSwapEvents;

		if (shouldNotify) {
			notify({
				type: 'success',
				message: `Swap Successful`,
				description: `Swapped ${formattedEvent.amountIn.toNum()} ${getCleanedMarketSymbol(
					spotMarketsSortedByMarketIndex[formattedEvent.inMarketIndex].symbol
				)} to ${formattedEvent.amountOut.toNum()} ${getCleanedMarketSymbol(
					spotMarketsSortedByMarketIndex[formattedEvent.outMarketIndex].symbol
				)}`,
				updatePrevious: true,
				id: toastId,
				lengthMs: 5000,
				subDescription: undefined,
				blockSubsequentToasts: true,
				action: undefined,
			});
			appEventEmitter.emit('successfulSwap', newSwapRecord);
		}

		if (newSwapRecord.user?.toString() === currentAccountPubKeyRef.current) {
			optimisticallyInsertShortTermRecord(
				queryClient,
				'swaps',
				currentAccountPubKeyRef.current,
				currentAuthorityRef.current,
				formattedEvent
			);
		}
	};

	const appendFundingPaymentRecord = (newRecord: FundingPaymentRecordEvent) => {
		const matchingUserKey = findAccountToAppendTo(newRecord.user);
		if (matchingUserKey === undefined) return;

		const formattedRecord = Serializer.Deserialize.UIFundingPayment(
			Serializer.Serialize.FundingPayment(newRecord)
		);
		if (newRecord.user?.toString() === currentAccountPubKeyRef.current) {
			optimisticallyInsertShortTermRecord(
				queryClient,
				'funding-payments',
				currentAccountPubKeyRef.current,
				currentAuthorityRef.current,
				formattedRecord
			);
		}
	};

	const appendLiquidationRecord = (newRecord: LiquidationRecordEvent) => {
		const matchingUserKey = findAccountToAppendTo(newRecord.user);
		if (matchingUserKey === undefined) return;

		const formattedRecord = Serializer.Deserialize.UILiquidation(
			Serializer.Serialize.Liquidation(newRecord)
		);
		if (newRecord.user?.toString() === currentAccountPubKeyRef.current) {
			optimisticallyInsertShortTermRecord(
				queryClient,
				'liquidations',
				currentAccountPubKeyRef.current,
				currentAuthorityRef.current,
				formattedRecord
			);
		}
	};

	const appendSettlePnlRecord = (newRecord: SettlePnlRecordEvent) => {
		const matchingUserKey = findAccountToAppendTo(newRecord.user);
		if (matchingUserKey === undefined) return;

		const formattedRecord = Serializer.Deserialize.UISettlePnl(
			Serializer.Serialize.SettlePnl(newRecord)
		);
		if (newRecord.user?.toString() === currentAccountPubKeyRef.current) {
			optimisticallyInsertShortTermRecord(
				queryClient,
				'settled-balances',
				currentAccountPubKeyRef.current,
				currentAuthorityRef.current,
				formattedRecord
			);
		}
	};

	const parseUserEvents = (event: WrappedEvent<EventType>) => {
		try {
			if (!eventFilter(event)) return;

			switch (event.eventType) {
				case 'DepositRecord':
					appendDepositRecord(event as DepositRecordEvent);
					break;
				case 'OrderRecord':
					appendOrderRecord(event as OrderRecordEvent);
					break;
				case 'SwapRecord':
					appendSwapRecord(event as Event<SwapRecordEvent>);
					break;
				case 'OrderActionRecord':
					// Fills get handled by the global order action event handler
					break;
				case 'FundingPaymentRecord':
					appendFundingPaymentRecord(event as FundingPaymentRecordEvent);
					break;
				case 'LiquidationRecord':
					appendLiquidationRecord(event as LiquidationRecordEvent);
					break;
				case 'SettlePnlRecord':
					appendSettlePnlRecord(event as SettlePnlRecordEvent);
					break;
			}
		} catch (e) {
			console.error('Error in Drift event listener: ', e);
			return;
		}
	};

	const parseGlobalFillEvents = (event: WrappedEvent<EventType>) => {
		try {
			const isActionRecord = event.eventType === 'OrderActionRecord';
			const isFillRecord =
				isActionRecord &&
				ENUM_UTILS.match((event as OrderActionRecord).action, OrderAction.FILL);

			if (!isFillRecord) {
				throw new Error(
					'Global fill event handler should only receive fill records'
				);
			}

			switch (event.eventType) {
				case 'OrderActionRecord':
					appendTradeRecord(event as Event<OrderActionRecord>);
					break;
			}
		} catch (e) {
			console.error('Error in Drift event listener: ', e);
			return;
		}
	};

	const eventFilter = (event: WrappedEvent<EventType>) =>
		EVENTS_TO_LISTEN_TO.includes(event.eventType as string);

	useEffect(() => {
		if (!eventSubjectContext.userEventObservable) return;
		if (!eventSubjectContext.globalFillEventObservable) return;

		const userEventSubscription =
			eventSubjectContext.userEventObservable.subscribe(parseUserEvents);

		const globalFillEventSubscription =
			eventSubjectContext.globalFillEventObservable.subscribe(
				parseGlobalFillEvents
			);

		return () => {
			userEventSubscription.unsubscribe();
			globalFillEventSubscription.unsubscribe();
		};
	}, [eventSubjectContext]);

	return;
};

export default useDriftMarketEventListener;
